import React, { useEffect, useState } from "react";
import { Alert, Box, Button, IconButton, Link, TextField } from "@mui/material";
import { VisibilityOutlined, VisibilityOffOutlined } from "@mui/icons-material";
import { trackEvent } from "@sbd-ctg/user-behavior-tracking";
import { formatSsoError, passwordLogin } from "../sso/ssoApi";
import LoginExperienceSwitcher from "./LoginExperienceSwitcher";
import {
  setForgotPasswordUsername,
  takeForgotPasswordUsername,
} from "../forgotPassword/forgotPasswordSession";
import { isPasswordResetRequiredError } from "../common/utils";

/**
 * Classic single-screen username + password login.
 */
export default function ClassicLogin({
  urlError = "",
  onClearUrlError,
  onLoginSuccess,
  setIsForgotPassword,
  experience,
  onExperienceChange,
  showExperienceSwitcher = false,
  onPasswordResetRequired,
}) {
  const [masked, setMasked] = useState(true);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [ssoRequired, setSsoRequired] = useState(false);
  const [username, setUsername] = useState(() => takeForgotPasswordUsername());
  const [password, setPassword] = useState("");
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (urlError && /unable to sign in with this email/i.test(urlError)) {
      onClearUrlError?.();
    }
  }, [urlError, onClearUrlError]);

  const displayUrlError =
    urlError && !/unable to sign in with this email/i.test(urlError)
      ? urlError
      : "";

  const handleForgotPassword = (e) => {
    e.preventDefault();
    if (!username) {
      setUsernameError(true);
      return;
    }
    setForgotPasswordUsername(username);
    setIsForgotPassword?.(true);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(false);
    setErrorMessage("");
    setSsoRequired(false);

    if (username === "" || password === "") {
      if (username === "") setUsernameError(true);
      if (password === "") setPasswordError(true);
      return;
    }

    setBusy(true);
    try {
      const loginId = username.includes("@")
        ? username.trim().toLowerCase()
        : username.trim();
      const token = await passwordLogin(loginId, password);
      await onLoginSuccess(token);
    } catch (err) {
      const resetRequired = isPasswordResetRequiredError(err);
      trackEvent("Web Login Failure", {
        event_name: "Web Login Failure",
        submitted_user_name: username ?? "",
        status: "Failure",
        description: resetRequired
          ? "Password reset required"
          : "Incorrect username/password combination.",
        login_experience: "classic",
      });
      if (resetRequired) {
        setForgotPasswordUsername(username.trim());
        onPasswordResetRequired?.(username.trim());
        return;
      }
      setError(true);
      if (err?.status === 403) {
        setSsoRequired(true);
        setErrorMessage(formatSsoError(err));
        setPasswordError(false);
      } else {
        setErrorMessage("");
      }
    } finally {
      setBusy(false);
    }
  };

  const errorHelperText = () => {
    if (error && !ssoRequired) {
      return "Incorrect username/password combination";
    }
    return "";
  };

  const userNameHelperText = () => {
    if (usernameError) {
      return "Please enter a valid username";
    }
    return errorHelperText();
  };

  const passwordHelperText = () => {
    if (passwordError) {
      return "Please enter a password";
    }
    return errorHelperText();
  };

  const handleEnterExplicitly = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleLogin(e);
    }
  };

  return (
    <Box>
      {displayUrlError && (
        <Alert severity="error" sx={{ mb: 2 }} variant="outlined">
          {displayUrlError}
        </Alert>
      )}
      {ssoRequired && errorMessage && (
        <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
          {errorMessage}{" "}
          {onExperienceChange && (
            <Link
              component="button"
              type="button"
              color="primary"
              className="forgot-password-btn"
              onClick={() => onExperienceChange("progressive")}
              data-testid="classic-sso-required-switcher"
            >
              Try our new login experience
            </Link>
          )}
        </Alert>
      )}
      <Box component="form" onSubmit={handleLogin} noValidate>
        <TextField
          fullWidth
          margin="normal"
          label="Username"
          variant="outlined"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            setUsernameError(false);
            setError(false);
            setSsoRequired(false);
            onClearUrlError?.();
          }}
          error={usernameError || (error && !ssoRequired)}
          helperText={userNameHelperText()}
          onKeyDown={handleEnterExplicitly}
          autoComplete="username"
          autoFocus
          slotProps={{
            input: {
              name: "username",
              "data-testid": "username-input",
            },
          }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Password"
          variant="outlined"
          type={masked ? "password" : "text"}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setPasswordError(false);
            setError(false);
            setSsoRequired(false);
          }}
          slotProps={{
            input: {
              name: "password",
              "data-testid": "password-input",
              endAdornment: (
                <IconButton onClick={() => setMasked(!masked)} edge="end">
                  {masked ? (
                    <VisibilityOutlined className="visibility-icon" />
                  ) : (
                    <VisibilityOffOutlined className="visibility-icon" />
                  )}
                </IconButton>
              ),
            },
          }}
          error={passwordError || (error && !ssoRequired)}
          helperText={passwordHelperText()}
          onKeyDown={handleEnterExplicitly}
          autoComplete="current-password"
          InputLabelProps={{ shrink: true }}
        />
        {setIsForgotPassword && (
          <Box
            mt={2}
            mr={1.3}
            textAlign="right"
            data-testid="forgot-password-box"
          >
            <Link
              component="button"
              type="button"
              color="primary"
              className="forgot-password-btn"
              onClick={handleForgotPassword}
              data-testid="forgot-password-link"
            >
              Forgot Password?
            </Link>
          </Box>
        )}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          className="button"
          disabled={busy}
          sx={{ mt: 5 }}
          data-testid="login-button"
        >
          {busy ? "Signing in…" : "Log in"}
        </Button>
      </Box>
      {showExperienceSwitcher && experience && onExperienceChange && (
        <LoginExperienceSwitcher
          experience={experience}
          onChange={onExperienceChange}
        />
      )}
    </Box>
  );
}
